import { z } from 'zod';
import { BLOCK_KEYS_MAX, BlockKeySchema } from './block-keys.js';
import { ConnectorErrorClassSchema } from './errors.js';
import type { NormalizeResult } from './facts.js';
import { PROFILE_URL_PATTERN } from './refs.js';
import { RawRecordSourceSchema, type StreamId, StreamIdSchema } from './sources.js';

/** Bekannte Capability-Schlüssel ([A] 3.1, E-p21); weitere Schlüssel bleiben im Muster zulässig. */
export const KNOWN_CAPABILITY_KEYS = [
  'network.read',
  'messages.read',
  'invitations.read',
  'engagement.read',
  'messages.send',
  'invitations.send',
  'post.publish',
  'posts.read',
  'comments.read',
  'calendar.read',
] as const;

const CLIPBOARD_MAX = 8000;
const PUBLISH_TEXT_MAX = 3000;
/** Deep-Link-Ziel: https, Host exakt `www.linkedin.com`, weder Zugangsdaten noch Port ([A] 3.2.7). */
const LINKEDIN_URL_PATTERN = /^https:\/\/www\.linkedin\.com(?:[/?#]\S*)?$/;

/** Opake, versionierte, streamgebundene Position; Konnektoren dürfen Schlüssel ergänzen. */
export const CursorSchema = z.looseObject({ v: z.int().min(1), stream: StreamIdSchema });
export type Cursor = z.infer<typeof CursorSchema>;

/** Position innerhalb eines Laufs (analog zum Cursor). */
export const PageTokenSchema = z.looseObject({ v: z.int().min(1), stream: StreamIdSchema });
export type PageToken = z.infer<typeof PageTokenSchema>;

const GapSchema = z
  .strictObject({ start: z.date(), end: z.date() })
  .refine((gap) => gap.start.getTime() < gap.end.getTime(), {
    message: 'gap.start muss vor gap.end liegen',
    path: ['start'],
  });

export const AdvanceSchema = z.discriminatedUnion('next', [
  z.strictObject({
    next: z.literal('page'),
    pageToken: PageTokenSchema,
    cursor: CursorSchema.nullable(),
  }),
  z.strictObject({
    next: z.literal('complete'),
    cursor: CursorSchema.nullable(),
    window: z.strictObject({ start: z.date(), end: z.date() }).exactOptional(),
    /**
     * Anzahl der **Kandidaten** der Überlappung (B-C1, V12-19): der zustandslose Konnektor sieht nicht, welche
     * Datensätze neu sind. Maßgeblich für `overlap_hit_count` ist T1 über `meta.overlapCandidateKeys` (Schnitt mit
     * den tatsächlich eingefügten Schlüsseln).
     */
    overlapHits: z.int().min(0),
  }),
  z.strictObject({ next: z.literal('saturated'), cursor: CursorSchema, gap: GapSchema }),
  z.strictObject({ next: z.literal('partial'), reason: z.literal('page_limit') }),
]);
export type Advance = z.infer<typeof AdvanceSchema>;

export const CapabilitySchema = z.strictObject({
  key: z.string().regex(/^[a-z]+(\.[a-z_]+)+$/),
  mode: z.enum(['api', 'manual', 'none']),
  latency: z.string().exactOptional(),
  lookback: z.string().exactOptional(),
  note: z.string().exactOptional(),
});
export type Capability = z.infer<typeof CapabilitySchema>;

export const AccountRefSchema = z.strictObject({
  accountId: z.string().min(1),
  externalAccountId: z.string().nullable(),
  selfProfileUrl: z.string().nullable(),
});
export type AccountRef = z.infer<typeof AccountRefSchema>;

export const AccountHealthSchema = z.strictObject({
  tokenValid: z.boolean(),
  /** `regulatedAt` der DMA-Einwilligung. */
  consentSince: z.date().nullable(),
  consentActive: z.boolean(),
  failure: ConnectorErrorClassSchema.exactOptional(),
});
export type AccountHealth = z.infer<typeof AccountHealthSchema>;

/** Rohdatensatz nach Eingangsminimierung; `payload` muss vorhanden sein (`null` ist erlaubt). */
export const RawRecordInputSchema = z
  .strictObject({
    source: RawRecordSourceSchema,
    sourceKey: z.string().min(1).max(512),
    kind: z.string().min(1),
    method: z.string().exactOptional(),
    resourceId: z.string().exactOptional(),
    sourceTime: z.date().exactOptional(),
    capturedAt: z.date().exactOptional(),
    payload: z.unknown(),
    /**
     * Schlüssel für die `erasure_block`-Prüfung (Klartext nur im Speicher), nur im kanonischen Format
     * (`canonicalBlockKey`, B-C1, V12-18); höchstens `BLOCK_KEYS_MAX` (256).
     */
    blockKeys: z.array(BlockKeySchema).max(BLOCK_KEYS_MAX),
    pageNo: z.int().min(0),
    rowNo: z.int().min(0),
  })
  .refine((record) => record.payload !== undefined, {
    message: 'payload fehlt (null ist erlaubt)',
    path: ['payload'],
  });
export type RawRecordInput = z.infer<typeof RawRecordInputSchema>;

/** Lesesicht auf einen gespeicherten Rohdatensatz für `normalize` (R-12). */
export const RawRecordViewSchema = z.strictObject({
  id: z.string().min(1),
  source: RawRecordSourceSchema,
  sourceKey: z.string().min(1),
  kind: z.string().min(1),
  method: z.string().nullable(),
  resourceId: z.string().nullable(),
  sourceTime: z.date().nullable(),
  capturedAt: z.date().nullable(),
  payload: z.unknown(),
});
export type RawRecordView = z.infer<typeof RawRecordViewSchema>;

const OWNERS_MAX = 64;
/** Kandidaten sind an keine Seitengröße gebunden (Konnektoren haben verschiedene); die Grenze schützt nur die Größe der Metadaten. */
const OVERLAP_CANDIDATES_MAX = 1000;
const distinct = (values: readonly string[]): boolean => new Set(values).size === values.length;

/**
 * Seitenangaben (B-C1, V12-19): typisierte Kernfelder für die Transaktion T1, weitere konnektorspezifische Schlüssel
 * (z. B. `processedAt`-Minimum/Maximum, `elementCount`) bleiben erlaubt (passthrough).
 * - `owners`: `owner`-URNs der Seite für Kontobindung und -prüfung in T1 (`account_mismatch`).
 * - `overlapCandidateKeys`: `sourceKey`s im Überlappungsbereich; T1 zählt `inserted ∩ Kandidaten`.
 * - `unorderedGapCandidate`: Spanne `[start, end)` (Epoche in ms) bei ungeordneten Seiten; T1 schreibt
 *   `data_gap(possible_unordered)`, wenn `advance` `next = 'page'` liefert.
 */
export const PageMetaSchema = z.looseObject({
  owners: z
    .array(z.string().min(1))
    .max(OWNERS_MAX)
    .refine(distinct, { message: 'owners müssen eindeutig sein' })
    .exactOptional(),
  overlapCandidateKeys: z
    .array(z.string().min(1))
    .max(OVERLAP_CANDIDATES_MAX)
    .refine(distinct, { message: 'overlapCandidateKeys müssen eindeutig sein' })
    .exactOptional(),
  unorderedGapCandidate: z
    .strictObject({ start: z.int().min(0), end: z.int().min(0) })
    .refine((gap) => gap.start < gap.end, {
      message: 'start muss vor end liegen',
      path: ['start'],
    })
    .nullable()
    .exactOptional(),
});
export type PageMeta = z.infer<typeof PageMetaSchema>;

export const FetchPageResultSchema = z.strictObject({
  records: z.array(RawRecordInputSchema),
  meta: PageMetaSchema,
  httpStatus: z.int().min(100).max(599),
});
export type FetchPageResult = z.infer<typeof FetchPageResultSchema>;

export const StoreResultSchema = z.strictObject({
  inserted: z.int().min(0),
  duplicates: z.int().min(0),
  blocked: z.int().min(0),
});
export type StoreResult = z.infer<typeof StoreResultSchema>;

export const ConnectorSettingsSchema = z
  .record(
    z.string(),
    z.union([z.string(), z.number(), z.boolean(), z.array(z.string()).readonly()]),
  )
  .readonly();
export type ConnectorSettings = z.infer<typeof ConnectorSettingsSchema>;

/**
 * Laufkontext. `run` (B-C1, V12-19) ersetzt die Konvention `settings['run.id']`: Laufkennung (`fetch_run.id`) und die
 * auf den Lauf festgelegte Token-Generation, die `startRun`/`fetchPage` des zustandslosen Konnektors sonst nicht kennen.
 */
export const RunContextSchema = z.strictObject({
  now: z.date(),
  consentSince: z.date().nullable(),
  settings: ConnectorSettingsSchema,
  bootstrap: z.boolean(),
  run: z.strictObject({ id: z.uuid(), tokenGeneration: z.int().min(0) }),
});
export type RunContext = z.infer<typeof RunContextSchema>;

export const NormalizeContextSchema = z.strictObject({
  selfUrn: z.string().nullable(),
  selfProfileUrl: z.string().nullable(),
  normalizerVersion: z.int().min(0),
});
export type NormalizeContext = z.infer<typeof NormalizeContextSchema>;

const clipboardText = z.string().max(CLIPBOARD_MAX).exactOptional();

/** Aktion, die der Mensch selbst ausführt (Deep-Link, Text in die Zwischenablage; [A] 3.2.7). */
export const ActionRequestSchema = z.discriminatedUnion('kind', [
  z.strictObject({
    kind: z.literal('open_profile'),
    profileUrl: z.string().regex(PROFILE_URL_PATTERN),
    clipboardText,
  }),
  z.strictObject({
    kind: z.literal('open_conversation'),
    conversationExternalId: z.string().min(1),
    clipboardText,
  }),
]);
export type ActionRequest = z.infer<typeof ActionRequestSchema>;

export const ActionPlanSchema = z.strictObject({
  url: z.string().regex(LINKEDIN_URL_PATTERN),
  clipboardText,
});
export type ActionPlan = z.infer<typeof ActionPlanSchema>;

/** Beitrag über die zweite LinkedIn-App ([TP5] 4.4); die Länge 3.000 ist Annahme B11. */
export const PublishRequestSchema = z.strictObject({
  text: z.string().min(1).max(PUBLISH_TEXT_MAX),
  visibility: z.enum(['PUBLIC', 'CONNECTIONS']),
  link: z
    .strictObject({
      url: z.url({ protocol: /^https$/ }),
      title: z.string().exactOptional(),
      description: z.string().exactOptional(),
    })
    .exactOptional(),
});
export type PublishRequest = z.infer<typeof PublishRequestSchema>;

/** Ergebnis wird nie automatisch in einen weiteren Aufruf übersetzt ([TP5] Abschnitt 9). */
export const PublishOutcomeSchema = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('created'), urn: z.string().nullable() }),
  z.strictObject({
    kind: z.literal('rejected'),
    httpStatus: z.int().min(400).max(599),
    errorClass: z.enum(['auth', 'permanent', 'rate_limited']),
  }),
  z.strictObject({ kind: z.literal('not_sent'), reason: z.literal('connect_failed') }),
  z.strictObject({
    kind: z.literal('unknown'),
    reason: z.enum(['timeout', 'server_error', 'aborted']),
  }),
]);
export type PublishOutcome = z.infer<typeof PublishOutcomeSchema>;

/** Zugangsdaten nur über diesen injizierten Anbieter; nie über den Schritt hinaus gecacht. */
export interface CredentialsProvider {
  /** Weicht die aktuelle Generation ab: `ConnectorError('aborted', 'stale_token')`. */
  getAccessToken(
    accountId: string,
    expectedGeneration: number,
    signal: AbortSignal,
  ): Promise<string>;
  currentGeneration(accountId: string): Promise<number>;
}

/**
 * Konnektor-Schnittstelle: seitenweise (jede Seite ein DBOS-Schritt), zustandslos, schreibt nie in
 * die Datenbank. `publish` gibt es nur im Konnektor `linkedin-share`.
 */
export interface Connector {
  readonly id: string;
  readonly network: string;
  readonly provenance: 'official';
  readonly normalizerVersion: number;
  streams(): StreamId[];
  capabilities(account: AccountRef): Capability[];
  health(
    account: AccountRef,
    creds: CredentialsProvider,
    signal: AbortSignal,
  ): Promise<AccountHealth>;
  /** Rein. */
  startRun(stream: StreamId, cursor: Cursor | null, ctx: RunContext): PageToken;
  fetchPage(
    account: AccountRef,
    token: PageToken,
    creds: CredentialsProvider,
    signal: AbortSignal,
  ): Promise<FetchPageResult>;
  /** Rein. */
  advance(
    token: PageToken,
    page: FetchPageResult,
    stored: StoreResult,
    cursor: Cursor | null,
    ctx: RunContext,
  ): Advance;
  /** Falsche Version oder falscher Strom: `ConnectorError('protocol', …)`. */
  parseCursor(value: unknown): Cursor;
  /** Rein, deterministisch. */
  normalize(raw: RawRecordView, ctx: NormalizeContext): NormalizeResult;
  /** Rein; nur Deep-Link und Text. */
  prepareAction(req: ActionRequest): ActionPlan;
  publish?(account: AccountRef, req: PublishRequest, signal: AbortSignal): Promise<PublishOutcome>;
}
